import { useMemo } from 'react';
import translations from '../i18n/translations.json';
import { detectBrowserLanguage } from '../utils/browserLanguage';

type Language = 'da' | 'en';
type TranslationKey = string;

interface TranslationObject {
  [key: string]: string | TranslationObject;
}

type Translations = Record<Language, TranslationObject>;

/**
 * Hook for handling translations with interpolation support.
 * 
 * Default language is detected from browser settings:
 * - If browser is English → 'en'
 * - If browser is Danish → 'da'
 * - Any other language → 'en' (fallback)
 * 
 * @param language - The language code ('da' or 'en'). If not provided, detects from browser.
 * @returns Translation function and current language
 */
export const useTranslation = (language?: Language) => {
  // Resolve language: use provided value or detect from browser
  const resolvedLanguage = language ?? detectBrowserLanguage();
  const t = useMemo(() => {
    const getNestedValue = (obj: TranslationObject | undefined, path: string): string => {
      if (!obj) return path;
      
      const keys = path.split('.');
      let current: string | TranslationObject | undefined = obj;
      
      for (const key of keys) {
        if (current && typeof current === 'object') {
          current = (current as TranslationObject)[key];
        } else {
          return path;
        }
      }

      return typeof current === 'string' ? current : path;
    };

    return (key: TranslationKey, params?: Record<string, string | number> & { defaultValue?: string }): string => {
      const typedTranslations = translations as Translations;
      const langTranslations = typedTranslations[resolvedLanguage];
      
      // Safety check: if language translations don't exist, fall back to 'en'
      if (!langTranslations) {
        const fallbackTranslations = typedTranslations['en'];
        const translation = getNestedValue(fallbackTranslations, key);
        return translation;
      }
      
      const translation = getNestedValue(langTranslations, key);

      // If translation not found (returns the key), use defaultValue if provided
      const finalTranslation = (translation === key && params?.defaultValue) 
        ? params.defaultValue 
        : translation;

      if (!params) {
        return finalTranslation;
      }

      // Replace placeholders like {{agentName}} with actual values
      // Skip 'defaultValue' as it's not a placeholder
      return Object.entries(params)
        .filter(([paramKey]) => paramKey !== 'defaultValue')
        .reduce((text, [paramKey, paramValue]) => {
          return text.replace(new RegExp(`\\{\\{${paramKey}\\}\\}`, 'g'), String(paramValue));
        }, finalTranslation);
    };
  }, [resolvedLanguage]);

  return { t, language: resolvedLanguage };
};

export type { Language };
